/**
 * Whether this screen demands a signed-in session.
 *
 * It does in a deployed build. It does not when you run it locally, and that is
 * the point of this file.
 *
 * The reason is practical. The session cookie is scoped to samsurveyor.com, so a
 * page served from localhost cannot have one. Requiring a session locally means
 * the screen cannot be opened at all on the machine where it is being built,
 * which is exactly backwards.
 *
 * With sign-in off there is no session, so no signed-in endpoint can be called.
 * The screen says so plainly and shows everything that does not need an account:
 * the shell, all eight channel slots and what each needs, both overlays, and the
 * whole send-preview panel, which decides everything in the browser.
 *
 * To force the real behaviour locally, set REACT_APP_REQUIRE_AUTH=true.
 * To turn it off in a build, set REACT_APP_REQUIRE_AUTH=false — and be sure you
 * mean it, because it turns the screen into a page anybody can open.
 */

const flag = process.env.REACT_APP_REQUIRE_AUTH;
const localReviewHost = typeof window !== 'undefined'
    && ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);

export const REQUIRE_AUTH = flag === undefined
    ? (process.env.NODE_ENV === 'production' && !localReviewHost)
    : flag !== 'false';

/**
 * With no session there is nothing to send to a signed-in endpoint, so the
 * requests are not made at all.
 *
 * Making them anyway would fill the console with failures that look like faults
 * in the screen. They would be nothing of the kind: they would be a page with no
 * session asking questions only a session can ask.
 */
export const SIGNED_OUT_PREVIEW = !REQUIRE_AUTH;

/** What the screen says about itself while it is running this way. */
export const SIGNED_OUT_NOTE =
    'Running without sign-in, so no account data is loaded. '
    + 'Everything below that does not need an account is real: the eight channel '
    + 'slots and what each one needs, both overlays, and the send preview.';

/**
 * The account all development and testing runs against.
 *
 * Overturf Industrial, slug OID. Every seeded binding lives there, so the screen
 * has to be looking at the same account or it shows an empty list and nobody can
 * tell whether the fault is the data or the code.
 *
 * It is sent as an `X-Org-ID` header. That header is not trusted on its own: the
 * service accepts a named account only after checking that the verified session
 * really belongs to it. So this chooses between accounts a person already has;
 * it cannot reach one they do not.
 *
 * **It is never applied to an ordinary production build.** A deployed screen
 * pinned to one account would show every customer somebody else's channels. It
 * applies only when sign-in is off, which is the local case, or when somebody has
 * named an account deliberately with REACT_APP_ORG_ID.
 */
const ORG_FROM_ENV = (process.env.REACT_APP_ORG_ID || '').trim();

const DEVELOPMENT_ORG_ID = '897afad2-1578-4076-aea8-c55455edc9ee';

export const WORKING_ORG_ID = ORG_FROM_ENV || (REQUIRE_AUTH ? '' : DEVELOPMENT_ORG_ID);

/** What to call it on screen, so nobody has to recognise a uuid. */
export const WORKING_ORG_LABEL = WORKING_ORG_ID === DEVELOPMENT_ORG_ID ? 'OID' : WORKING_ORG_ID;
